import React from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { ARTICLES, getArticle } from "@/content/articles";
import { ShareArticle } from "@/components/shared/ShareArticle";
import SaveResource from "@/components/shared/SaveResource";
import { useMember } from "@/context/MemberContext";

const focusRing = "rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background";

function Inline({ text }) {
  return text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.length > 2 && part.startsWith("*") && part.endsWith("*")) return <em key={i}>{part.slice(1, -1)}</em>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function Block({ block }) {
  const lines = block.split("\n");
  if (lines.every((l) => l.startsWith("- "))) {
    return (
      <ul className="mt-5 list-disc pl-6 space-y-3 text-base md:text-lg leading-relaxed text-foreground/90">
        {lines.map((l, i) => <li key={i}><Inline text={l.slice(2)} /></li>)}
      </ul>
    );
  }
  if (block.startsWith("# ")) {
    return <h1 data-testid="article-title" className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-balance">{block.slice(2)}</h1>;
  }
  if (block.startsWith("## ")) {
    return <h2 className="font-display text-2xl md:text-3xl leading-tight tracking-tight mt-12 text-balance">{block.slice(3)}</h2>;
  }
  if (/^\*[^*].*\*$/.test(block)) {
    return <p data-testid="article-category-line" className="eyebrow !normal-case mt-4 italic">{block.slice(1, -1)}</p>;
  }
  return <p className="mt-5 text-base md:text-lg leading-relaxed text-foreground/90"><Inline text={block} /></p>;
}

function BackLink({ testid }) {
  return (
    <Link to="/learn" data-testid={testid} className={`inline-flex items-center gap-2 eyebrow hover:text-foreground ${focusRing}`}>
      <ChevronLeft className="h-3 w-3" /> Learning Center
    </Link>
  );
}

export default function Article() {
  const { user: member } = useMember();
  const { slug } = useParams();
  const article = getArticle(slug);

  if (!article) {
    return <div className="container-x py-32 text-center text-muted-foreground" data-testid="article-not-found">Article not found.</div>;
  }

  return (
    <article className="container-x py-16 md:py-24" data-testid="article-page">
      <div className="mx-auto max-w-[68ch] break-words">
        <BackLink testid="article-back-top" />
        <div className="mt-8">
          {article.body.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean).map((b, i) => (
            <React.Fragment key={i}>
              <Block block={b} />
              {i === 1 && <ShareArticle key={`${article.slug}-top`} title={article.title} slug={article.slug} position="top" />}
            </React.Fragment>
          ))}
        </div>
        <ShareArticle key={`${article.slug}-bottom`} title={article.title} slug={article.slug} position="bottom" />
        <SaveResource key={member?.user_id || "guest"} resourceId={"article-"+article.slug}/><section className="rounded-lg border bg-card p-6 mt-10"><h2 className="font-display text-2xl">Continue your learning</h2><p className="mt-4 text-sm text-muted-foreground">Explore the article learning check to reflect on what you've read. It does not award a certificate.</p><Link className="btn-outline mt-5" to="/learn/article-learning-check">Open Article Learning Check</Link></section>
        <div className="hairline my-12" />
        <nav aria-labelledby="related-articles-heading" data-testid="related-articles">
          <h2 id="related-articles-heading" className="font-display text-2xl md:text-3xl tracking-tight">Related Articles</h2>
          <ul className="mt-5 space-y-3">
            {ARTICLES.filter((a) => a.category === article.category && a.slug !== article.slug).map((a) => (
              <li key={a.slug}>
                <Link to={`/learn/articles/${a.slug}`} data-testid={`related-${a.slug}`} className={`text-base md:text-lg font-semibold hover:underline break-words ${focusRing}`}>
                  {a.title}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="hairline my-12" />
        <BackLink testid="article-back-bottom" />
      </div>
    </article>
  );
}
